import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Star, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import TeacherLoginDialog from '../../components/admin/TeacherLoginDialog';

const TeachersManagement = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subjects: '',
    languages_spoken: '',
    experience_years: 0,
    rate_per_hour: 0,
    photo_url: '',
    bio: '',
    qualifications: ''
  });

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/teachers`);
      setTeachers(response.data);
    } catch (error) {
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingTeacher(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subjects: '',
      languages_spoken: '',
      experience_years: 0,
      rate_per_hour: 0,
      photo_url: '',
      bio: '',
      qualifications: ''
    });
    setShowModal(true);
  };

  const openEditModal = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      name: teacher.name,
      email: teacher.email,
      phone: teacher.phone,
      subjects: teacher.subjects.join(', '),
      languages_spoken: (teacher.languages_spoken || []).join(', '),
      experience_years: teacher.experience_years,
      rate_per_hour: teacher.rate_per_hour,
      photo_url: teacher.photo_url || '',
      bio: teacher.bio,
      qualifications: teacher.qualifications
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        subjects: formData.subjects.split(',').map(s => s.trim()).filter(s => s),
        languages_spoken: formData.languages_spoken.split(',').map(s => s.trim()).filter(s => s),
        experience_years: parseInt(formData.experience_years),
        rate_per_hour: parseFloat(formData.rate_per_hour)
      };

      if (editingTeacher) {
        await axios.put(`${API}/teachers/${editingTeacher.id}`, payload);
        toast.success('Teacher updated successfully');
      } else {
        await axios.post(`${API}/teachers`, payload);
        toast.success('Teacher created successfully');
      }

      setShowModal(false);
      fetchTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (teacherId) => {
    if (!confirm('Are you sure you want to delete this teacher?')) return;

    try {
      await axios.delete(`${API}/teachers/${teacherId}`);
      toast.success('Teacher deleted successfully');
      fetchTeachers();
    } catch (error) {
      toast.error('Failed to delete teacher');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-slate-600">Loading teachers...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">Teachers Management</h1>
          <p className="text-lg text-slate-600">Add, edit, and manage teachers</p>
        </div>
        <Button
          onClick={openAddModal}
          className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
            data-testid="add-teacher-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Teacher
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teachers.map((teacher, index) => (
          <motion.div
            key={teacher.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-8 h-8 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-1">{teacher.name}</CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        <span className="text-sm font-semibold">{teacher.rating.toFixed(1)}</span>
                      </div>
                      <span className="text-xs text-slate-500">({teacher.total_ratings})</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {teacher.subjects.map(subject => (
                    <Badge key={subject} className="bg-violet-100 text-violet-700 border-violet-300">
                      {subject}
                    </Badge>
                  ))}
                </div>

                {teacher.languages_spoken?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {teacher.languages_spoken.map(language => (
                      <Badge key={language} className="bg-emerald-100 text-emerald-700 border-emerald-300">
                        {language}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="space-y-1 text-sm text-slate-600">
                  <p><strong>Email:</strong> {teacher.email}</p>
                  <p><strong>Phone:</strong> {teacher.phone}</p>
                  <p><strong>Experience:</strong> {teacher.experience_years} years</p>
                  <p><strong>Rate:</strong> ₹{teacher.rate_per_hour}/hour (1 student)</p>
                  {teacher.pricing_tiers?.length > 1 && (
                    <p data-testid={`teacher-group-pricing-${teacher.id}`}><strong>Groups:</strong> up to {teacher.pricing_tiers[teacher.pricing_tiers.length - 1].students} students</p>
                  )}
                  <p>
                    <strong>Portal:</strong>{' '}
                    {teacher.login_user_id
                      ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300" data-testid={`teacher-login-active-${teacher.id}`}>Login active</Badge>
                      : <span className="text-slate-400">No login yet</span>}
                  </p>
                </div>

                <TeacherLoginDialog teacher={teacher} onDone={fetchTeachers} />

                <div className="flex gap-2">
                  <Link to={`/admin/teachers/${teacher.id}/availability`} className="flex-1">
                    <Button variant="outline" className="w-full rounded-full" size="sm">
                      Manage Availability
                    </Button>
                  </Link>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => openEditModal(teacher)}
                    variant="outline"
                    className="flex-1 rounded-full"
                    size="sm"
                  data-testid={`edit-teacher-${teacher.id}`}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(teacher.id)}
                    variant="outline"
                    className="flex-1 rounded-full text-red-600 hover:bg-red-50"
                    size="sm"
                  data-testid={`delete-teacher-${teacher.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {teachers.length === 0 && (
        <Card className="border-0 shadow-lg">
          <CardContent className="py-12 text-center">
            <p className="text-slate-600">No teachers found. Add your first teacher!</p>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent key={editingTeacher?.id || 'new'} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Subjects (comma-separated) *</Label>
                <Input
                  placeholder="Physics, Chemistry, Mathematics"
                  value={formData.subjects}
                  onChange={(e) => setFormData({...formData, subjects: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Languages Spoken (comma-separated)</Label>
              <Input
                placeholder="English, Hindi"
                value={formData.languages_spoken}
                onChange={(e) => setFormData({...formData, languages_spoken: e.target.value})}
                data-testid="teacher-languages-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Experience (years) *</Label>
                <Input
                  type="number"
                  value={formData.experience_years}
                  onChange={(e) => setFormData({...formData, experience_years: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Rate per Hour (₹) *</Label>
                <Input
                  type="number"
                  value={formData.rate_per_hour}
                  onChange={(e) => setFormData({...formData, rate_per_hour: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Photo URL (optional)</Label>
              <Input
                value={formData.photo_url}
                onChange={(e) => setFormData({...formData, photo_url: e.target.value})}
                placeholder="https://example.com/photo.jpg"
              />
            </div>

            <div className="space-y-2">
              <Label>Bio *</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                rows={3}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Qualifications *</Label>
              <Textarea
                value={formData.qualifications}
                onChange={(e) => setFormData({...formData, qualifications: e.target.value})}
                rows={2}
                required
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-violet-600 hover:bg-violet-700 rounded-full">
                {editingTeacher ? 'Update Teacher' : 'Add Teacher'}
              </Button>
              <Button
                type="button"
                onClick={() => setShowModal(false)}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeachersManagement;